import React, {useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from "react";
import Grid2 from "@mui/material/Unstable_Grid2";
import {Box, Card, Checkbox, CircularProgress, Container, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, Popover, Skeleton, Tooltip, Typography} from "@mui/material";
import Button from "../../../../../../../components/base/button/button";
import {FilterSectionProps, Schema} from "./index";
import Form, {FinalForm, MuiRFF, ReactFinalForm, Yup} from "../../../../../../../components/form";
import Utils, {createUUId, deepEqual} from "../../../../../../../../core/services/utils/utils";
// @ts-ignore
import {ReactComponent as CheckboxIcon} from "../../../../../../../../assets/images/check-box/checkbox.svg";
// @ts-ignore
import {ReactComponent as ActiveCheckboxIcon} from "../../../../../../../../assets/images/check-box/checkbox-active.svg";
import useSavedComponentsState from "../../../../../../../hooks/use-saved-components-state";
import {DashboardViewFilterSectionFilters} from "../../types";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import {useTranslation} from "react-i18next";
import axios, {CancelToken} from "axios";
import ValidateMessages from "../../../../../../../../core/models/static/texts/vallidate-messages";
import Dialog from "../../../../../../../components/base/dialog";
import IconButton from "../../../../../../../components/base/button/icon_button";
import {DeleteOutline, EditOutlined, RefreshRounded, StarBorderOutlined, StarRounded} from "@mui/icons-material";
import {hasRequiredForcedFilters} from "../../utils/forced-filters";

export interface DashboardFilterTemplate {
    filters: DashboardViewFilterSectionFilters;
    id: string;
    name: string;
    isDefault: boolean;
}

export interface CachedDashboardFilterTemplates {
    templates?: DashboardFilterTemplate[],
    autoSaveChanges?: boolean,
    createdFromCachedFiltersV1?: boolean,
}

export interface DashboardViewFilterSectionActionsProps extends ReactFinalForm.FormRenderProps<Schema, Partial<Schema>>, FilterSectionProps {
    hasValues: (values: Schema) => boolean;
    activeValues: [keyof Schema, any][];
    generateSchemaValuesFromFilters: (filters: DashboardViewFilterSectionFilters) => Partial<Schema>;
    generateFilterValuesFromSchema: (values: Partial<Schema>) => DashboardViewFilterSectionFilters;
}

export interface DashboardViewFilterSectionActionsRef {
    onFormSubmitted: (values: Schema, filters: DashboardViewFilterSectionFilters) => void;
}

const DashboardViewFilterSectionActions: React.FC<DashboardViewFilterSectionActionsProps> = ({
                                                                                                 initialValues,
                                                                                                 onClear,
                                                                                                 form,
                                                                                                 disabled,
                                                                                                 values,
                                                                                                 submitting,
                                                                                                 allowApplyingWithoutChange,
                                                                                                 dashboardId,
                                                                                                 canSaveFilters,
                                                                                                 hasValues: hasValuesCallback,
                                                                                                 generateSchemaValuesFromFilters,
                                                                                                 generateFilterValuesFromSchema,
                                                                                                 invalid,
                                                                                                 forcedFilterRequirements,
                                                                                             }, ref) => {
    const [loading, setLoading] = React.useState<boolean>(canSaveFilters ?? false);
    const [templates, setTemplates] = React.useState<DashboardFilterTemplate[]>([]);
    const [selectedTemplateId, setSelectedTemplateId] = React.useState<DashboardFilterTemplate['id']>();
    const [autoSaveChanges, setAutoSaveChanges] = React.useState<boolean>(true);
    const isMounted = useIsMounted();
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.dashboard-v2.edit-widget-filters'});
    const clearDisabled = hasRequiredForcedFilters(forcedFilterRequirements);

    /**
     * Resets the templates and selected template when the dashboardId changes to avoid showing templates from a previous dashboard while loading the new ones.
     */
    React.useEffect(() => {
        setTemplates([]);
        setSelectedTemplateId(undefined);
    }, [dashboardId])

    const {highestVersion: getCachedDashboardFilterTemplates, save: saveCachedDashboardFilterTemplates} = useSavedComponentsState<CachedDashboardFilterTemplates>({
        name: `custom-dashboard-filters-templates-${dashboardId}`,
        version: '1.0.0'
    });

    const {highestVersion: getLegacyFilters} = useSavedComponentsState<{ filters?: DashboardViewFilterSectionFilters, [p: string]: any }>({
        name: `custom-dashboards-${dashboardId}`,
        version: '1.0.0'
    });

    const hasValues = useMemo(() => hasValuesCallback(values), [hasValuesCallback, values]);

    const formRef = useRef<FinalForm.FormApi<Schema, Partial<Schema>> | null>(form);
    formRef.current = form;

    /**
     * Sets the selected template and applies its filters to the form.
     * @param template The template to select and apply.
     */
    const setSelectedTemplate = useCallback((template?: DashboardFilterTemplate, setFilters?: boolean) => {
        setSelectedTemplateId(template?.id);
        if (setFilters !== false && template) {
            const filters = template?.filters ?? {};
            const schemaValues = generateSchemaValuesFromFilters(filters);
            formRef.current?.restart(schemaValues);
            void formRef.current?.submit();
        }
    }, [generateSchemaValuesFromFilters]);


    /**
     * Sets the templates and saves them to cache.
     */
    const setTemplatesAndSaveToCache = useCallback((updated: React.SetStateAction<DashboardFilterTemplate[]>) => {
        setTemplates(prevState => {
            const newTemplates = (typeof updated === 'function' ? updated(prevState) : updated);
            if (deepEqual(
                newTemplates.map(e => ({...e, filters: generateFilterValuesFromSchema(generateSchemaValuesFromFilters(e.filters))})),
                prevState.map(e => ({...e, filters: generateFilterValuesFromSchema(generateSchemaValuesFromFilters(e.filters))})))
            ) {
                return prevState;
            }
            void saveCachedDashboardFilterTemplates((prevState) => {
                return ({
                    templates: newTemplates,
                    autoSaveChanges: prevState?.autoSaveChanges,
                    createdFromCachedFiltersV1: prevState?.createdFromCachedFiltersV1,
                })
            });
            return newTemplates;
        });
    }, [generateFilterValuesFromSchema, generateSchemaValuesFromFilters, saveCachedDashboardFilterTemplates]);

    /**
     * Sets the auto-apply filters on the selected template and saves the preference to cache.
     */
    const setAutoSaveChangesAndSaveOnCache = useCallback(async (value: boolean) => {
        setAutoSaveChanges(value);
        void saveCachedDashboardFilterTemplates((prevState => ({
            templates: prevState?.templates,
            autoSaveChanges: value,
            createdFromCachedFiltersV1: prevState?.createdFromCachedFiltersV1,
        })));
    }, [saveCachedDashboardFilterTemplates]);

    /**
     * Creates a new template from legacy filters and saves it to cache.
     */
    const createTemplateFromLegacyFilter = useCallback(async (legacyFilters: DashboardViewFilterSectionFilters) => {
        const [cachedState, newTemplate] = await new Promise<[CachedDashboardFilterTemplates, NonUndefined<CachedDashboardFilterTemplates['templates']>[0]]>(r => {
            void saveCachedDashboardFilterTemplates(prevState => {
                const newTemplate = {id: createUUId(), name: 'Default Filters', filters: legacyFilters, isDefault: !prevState?.templates?.length};
                const newState = ({
                    templates: [...prevState?.templates ?? [], newTemplate],
                    autoSaveChanges: prevState?.autoSaveChanges ?? true,
                    createdFromCachedFiltersV1: true,
                })
                r([newState, newTemplate]);
                return newState;
            });
        })
        setTemplates(cachedState?.templates ?? []);
        setAutoSaveChanges(cachedState?.autoSaveChanges ?? true);
        setSelectedTemplate(newTemplate);
    }, [saveCachedDashboardFilterTemplates, setSelectedTemplate]);

    /**
     * Loads the filters from the server and sets them in the query state when the dashboard is selected.
     */
    const loadCachedFilterTemplatesAndApplyDefaultFilters = useCallback(async (cancelToken: CancelToken) => {
        setLoading(true);
        const [cachedFilterTemplates, legacyFilters] = await Promise.all([
            getCachedDashboardFilterTemplates(cancelToken),
            getLegacyFilters(cancelToken),
        ]);
        if (!isMounted())
            return;
        if (cachedFilterTemplates?.data) {
            setTemplates(cachedFilterTemplates?.data?.templates || []);
            setAutoSaveChanges(cachedFilterTemplates?.data?.autoSaveChanges ?? true);
            setSelectedTemplate(cachedFilterTemplates?.data?.templates?.find(template => template.isDefault) ?? cachedFilterTemplates?.data?.templates?.[0])
        }
        if (legacyFilters?.data?.filters) {
            // Migrate legacy filters to a new template if there are no cached templates
            if (!cachedFilterTemplates?.data?.createdFromCachedFiltersV1) {
                void createTemplateFromLegacyFilter(legacyFilters.data.filters);
            }
        }
        setLoading(false);
    }, [createTemplateFromLegacyFilter, getCachedDashboardFilterTemplates, getLegacyFilters, isMounted, setSelectedTemplate, dashboardId]);

    /**
     * Loads the filters when the selected dashboard changes
     */
    useEffect(() => {
        const cancelTokenSource = axios.CancelToken.source();
        void loadCachedFilterTemplatesAndApplyDefaultFilters(cancelTokenSource.token);
        return () => cancelTokenSource.cancel();
    }, [loadCachedFilterTemplatesAndApplyDefaultFilters, dashboardId]);


    /**
     * Handles resetting all filters by setting them to undefined.
     */
    const handleReset = useCallback((form: FinalForm.FormApi<Schema, Partial<Schema>>) => {
        form.restart(initialValues);
    }, [initialValues]);

    /**
     * Handles clearing all filters by setting them to initial values.
     */
    const handleClear = useCallback((form: FinalForm.FormApi<Schema, Partial<Schema>>) => {
        form.restart({});
        if (onClear) {
            onClear();
        } else {
            form.submit()
        }
    }, [onClear]);

    /**
     * Handles the form submission to auto-save changes to the selected template.
     */
    const onFormSubmitted = useCallback((submittedValues: Schema, filters: DashboardViewFilterSectionFilters) => {
        if (autoSaveChanges && selectedTemplateId) {
            setTemplatesAndSaveToCache((prevState) => prevState.map(template => template.id === selectedTemplateId ? {...template, filters: filters} : template));
        }
    }, [autoSaveChanges, selectedTemplateId, setTemplatesAndSaveToCache]);

    /**
     * Exposes the onFormSubmitted method to the parent component.
     */
    useImperativeHandle<DashboardViewFilterSectionActionsRef, DashboardViewFilterSectionActionsRef>(ref, () => ({
        onFormSubmitted,
    }), [onFormSubmitted])

    const same = useMemo(() => deepEqual({...values, mergeWithParentFilters: undefined}, {...initialValues, mergeWithParentFilters: undefined}), [initialValues, values]);

    return (
        <>
            <Box display={'flex'} justifyContent={{xs: 'center', smMd: 'space-between'}} alignItems={'center'} flexWrap={'wrap'}>
                {
                    canSaveFilters &&
                    <TemplateManagementCard
                        loading={loading}
                        selectedTemplateId={selectedTemplateId}
                        setSelectedTemplate={setSelectedTemplate}
                        disabled={disabled || !canSaveFilters}
                        templates={templates}
                        setTemplates={setTemplatesAndSaveToCache}
                        schemaValues={values}
                        generateFilterValuesFromSchema={generateFilterValuesFromSchema}
                    />
                }
                {
                    !canSaveFilters &&
                    <Box/>
                }
                <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1}}>
                    {
                        hasValues &&
                        <Button
                            type="button"
                            variant="outlined"
                            disabled={disabled || clearDisabled}
                            onClick={() => handleClear(form)}
                        >
                            Clear Filters
                        </Button>
                    }
                    {
                        !same &&
                        <Button
                            type="button"
                            variant="outlined"
                            disabled={disabled}
                            onClick={() => handleReset(form)}
                        >
                            Reset Changes
                        </Button>
                    }
                    <Box sx={{display: 'flex', flexFlow: 'column'}}>
                        <Button
                            type="button"
                            disabled={submitting || (Boolean(forcedFilterRequirements?.length) && invalid) || (same && !allowApplyingWithoutChange) || disabled}
                            variant="contained"
                            color="primary"
                            onClick={() => form.submit()}
                        >
                            Apply Filters
                        </Button>
                        {
                            invalid && Boolean(forcedFilterRequirements?.length) &&
                            <Typography variant="caption" color="error" sx={{mt: 0.5, maxWidth: 220}}>
                                {t(t => t.requirements.applyBlocked)}
                            </Typography>
                        }
                        {
                            selectedTemplateId && canSaveFilters &&
                            <FormControlLabel
                                label="Auto Save Changes"
                                slotProps={{typography: {variant: 'caption', color: disabled ? 'text.disabled' : 'text.primary', fontSize: 12}}}
                                disabled={disabled}
                                control={
                                    <Checkbox
                                        sx={{paddingBlock: 0.5}}
                                        disableRipple
                                        checked={autoSaveChanges}
                                        onChange={(e) => setAutoSaveChangesAndSaveOnCache(e.target.checked)}
                                        icon={<CheckboxIcon/>}
                                        checkedIcon={<ActiveCheckboxIcon/>}
                                    />
                                }
                            />
                        }
                    </Box>
                </Box>
            </Box>
        </>
    );
}

export default React.forwardRef<DashboardViewFilterSectionActionsRef, DashboardViewFilterSectionActionsProps>(DashboardViewFilterSectionActions);


interface TemplateManagementCardProps {
    templates: DashboardFilterTemplate[];
    setTemplates: React.Dispatch<React.SetStateAction<DashboardFilterTemplate[]>>;
    selectedTemplateId?: DashboardFilterTemplate['id'];
    setSelectedTemplate: (template: DashboardFilterTemplate | undefined, setFilters?: boolean) => void;
    disabled?: boolean;
    loading: boolean;
    schemaValues: Partial<Schema>;
    generateFilterValuesFromSchema: (values: Partial<Schema>) => DashboardViewFilterSectionFilters;
}

const TemplateManagementCard: React.FC<TemplateManagementCardProps> = ({
                                                                           templates,
                                                                           setTemplates,
                                                                           selectedTemplateId,
                                                                           setSelectedTemplate,
                                                                           disabled,
                                                                           loading,
                                                                           schemaValues,
                                                                           generateFilterValuesFromSchema
                                                                       }) => {
    const [saving, setSaving] = React.useState<boolean>(false);
    const [settingAsDefault, setSettingAsDefault] = useState<DashboardFilterTemplate['id']>();
    const [applyingChangesToTemplate, setApplyingChangesToTemplate] = useState<DashboardFilterTemplate['id']>();
    const [removingTemplate, setRemovingTemplate] = useState<DashboardFilterTemplate['id']>();
    const [dialogState, setDialogState] = useState<{ open: boolean; templateToEdit?: DashboardFilterTemplate }>({open: false, templateToEdit: undefined,});
    const [popover, setPopover] = React.useState<HTMLElement | null>(null);
    const popoverId = useRef(Utils.createUUID());
    const isMounted = useIsMounted();

    const selectedTemplate = useMemo(() => templates.find(template => template.id === selectedTemplateId), [selectedTemplateId, templates]);
    /**
     * Creates a new template from the current filters and saves it.
     */
    const createOrUpdateTemplate = useCallback(async (templateInfo: Partial<DashboardFilterTemplate>) => {
        const filters = generateFilterValuesFromSchema(schemaValues);
        const newTemplate = !templateInfo.id;
        setSaving(true);
        const template = await new Promise<DashboardFilterTemplate>(r => {
            setTemplates((prevState) => {
                if (newTemplate) {
                    const templateId = createUUId();
                    const template = {
                        id: templateId,
                        name: templateInfo.name || `Template ${prevState.length + 1}`,
                        filters: filters,
                        isDefault: !prevState.length,
                    } satisfies DashboardFilterTemplate;
                    r(template);
                    return [...prevState, template];
                } else {
                    const foundMerged = {...prevState.find(t => t.id === templateInfo.id) ?? {}, ...templateInfo} as DashboardFilterTemplate;
                    r(foundMerged);
                    return prevState.map(t => t.id === templateInfo.id ? foundMerged : t);
                }
            })
        })
        if (!isMounted())
            return;
        if (newTemplate) {
            setSelectedTemplate(template, false);
        }
        setSaving(false);
    }, [generateFilterValuesFromSchema, isMounted, schemaValues, setSelectedTemplate, setTemplates]);

    /**
     * Sets the given template as the default template.
     * @param template The template to set as default.
     */
    const setAsDefaultTemplate = useCallback(async (template: DashboardFilterTemplate) => {
        if (template.isDefault)
            return;
        setSettingAsDefault(template.id);
        await new Promise<void>(r => {
            setTemplates((prevState) => {
                r();
                return prevState.map(t => ({
                    ...t,
                    isDefault: t.id === template.id,
                }));
            });
        });
        if (!isMounted())
            return;
        setSettingAsDefault(undefined);
    }, [isMounted, setTemplates]);

    /**
     * Applies the current filters to the given template.
     */
    const applyCurrentFiltersToTemplate = useCallback(async (template: DashboardFilterTemplate) => {
        setApplyingChangesToTemplate(template.id);
        await new Promise<void>(r => {
            setTemplates((prevState) => {
                r();
                return prevState.map(t => t.id === template.id ? {
                    ...t,
                    filters: generateFilterValuesFromSchema(schemaValues),
                } : t);
            });
        });
        if (!isMounted())
            return;
        setApplyingChangesToTemplate(undefined)
    }, [generateFilterValuesFromSchema, isMounted, schemaValues, setTemplates]);

    /**
     * Removes the template with the given id.
     */
    const removeTemplate = useCallback((template: DashboardFilterTemplate) => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Are you sure you want to delete the template "<strong>{template.name}</strong>"?
                </p>
            </div>,
            {},
            async (decision) => {
                if (!decision)
                    return;
                const templateId = template.id;
                setRemovingTemplate(templateId);
                await new Promise<void>(r => {
                    setTemplates((prevState) => {
                        const newTemplates = prevState.filter(t => t.id !== templateId);
                        if (newTemplates.length === 0) {
                            setSelectedTemplate(undefined);
                        } else if (templateId === selectedTemplateId) {
                            const newSelected = newTemplates.find(e => e.isDefault) || newTemplates[0];
                            setSelectedTemplate(newSelected);
                        }
                        r();
                        return newTemplates;
                    });
                });
                if (!isMounted())
                    return;
                setRemovingTemplate(undefined);
            }
        );
    }, [isMounted, selectedTemplateId, setSelectedTemplate, setTemplates]);

    /** Closes the popover */
    const closePopover = React.useCallback(() => setPopover(null), []);

    /** Opens the popover */
    const openPopover = React.useCallback((e) => setPopover(e.currentTarget), []);

    /**
     * Selects the given template and closes the popover.
     */
    const selectTemplate = useCallback((template: DashboardFilterTemplate) => {
        if (template.id !== selectedTemplateId)
            setSelectedTemplate(template);
        closePopover();
    }, [setSelectedTemplate, selectedTemplateId, closePopover]);

    return (
        <>
            <Box sx={{display: 'flex', flexDirection: 'column'}}>
                <Card
                    sx={{padding: 1, paddingInline: 3, backgroundColor: 'background.default', width: 'fit-content', marginInline: {xs: 'auto', smMd: 'initial'}, ...(!disabled && {cursor: 'pointer'})}}
                    onClick={openPopover}>
                    <Typography variant="body2" color={selectedTemplateId ? 'text.primary' : 'text.secondary'}>
                        {
                            loading
                                ? "Loading Templates..."
                                : selectedTemplateId
                                    ? `Template: ${selectedTemplate?.name || ''}`
                                    : 'Select a Template'
                        }
                    </Typography>
                </Card>
                <Button variant="text" disabled={disabled || loading} onClick={() => setDialogState({open: true, templateToEdit: undefined})}>
                    {
                        saving
                            ? 'Creating Template...'
                            : "Create Template from Current Filters"
                    }
                </Button>
            </Box>
            <Popover
                id={!!popover ? popoverId.current : undefined}
                elevation={8}
                open={!!popover}
                onClose={closePopover}
                anchorReference={'anchorEl'}
                anchorEl={popover}
                anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                transformOrigin={{vertical: -8, horizontal: 'left'}}
            >
                <Container maxWidth={'xs'} sx={{paddingBlock: 2, maxHeight: 400, overflowY: 'auto', width: 600}} disableGutters>
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, width: '100%'}}>
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                            <Typography variant="body2" fontWeight={500} paddingInline={1}>
                                Available Templates
                            </Typography>
                            <Divider/>
                        </Box>
                        {
                            loading &&
                            <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, paddingInline: 1}}>
                                {
                                    Array.from({length: 3}).map((_, index) => (
                                        <Skeleton key={index} height={52} width={'100%'} sx={{transform: 'none'}}/>
                                    ))
                                }
                            </Box>
                        }
                        {
                            !loading && templates.length === 0 &&
                            <Typography variant="body2" color="text.secondary" textAlign={'center'} marginBlockStart={8} sx={{paddingInline: 1}}>
                                No templates available. Create a new template from the current filters.
                            </Typography>
                        }
                        {
                            !loading &&
                            <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, paddingInline: 1}}>
                                {
                                    templates.map((template) => (
                                        <Card
                                            key={template.id}
                                            variant={'outlined'}
                                            sx={{
                                                padding: 1,
                                                paddingInline: 2,
                                                borderRadius: 1,
                                                backgroundColor: (theme) => template.id === selectedTemplateId ? `rgba(${theme.palette.secondary.mainChannel} / ${theme.palette.action.focusOpacity})` : 'transparent',
                                                borderColor: (theme) => template.id === selectedTemplateId ? theme.palette.secondary.main : 'transparent',
                                                color: (theme) => template.id === selectedTemplateId ? theme.palette.secondary.main : theme.palette.text.primary,
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: 1,
                                            }}
                                            onClick={() => selectTemplate(template)}
                                        >
                                            <Typography variant="body2" fontWeight={template.id === selectedTemplateId ? 600 : 400} color={'inherit'}>
                                                {template.name ?? ""}
                                            </Typography>
                                            <Box sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5}}>
                                                <Tooltip title={applyingChangesToTemplate === template.id ? "Applying changes..." : "Apply Current Filters to this Template"} placement="top">
                                                    <Box component={'span'}>
                                                        <IconButton
                                                            size={'small'}
                                                            color={'secondary'}
                                                            disabled={disabled || !!applyingChangesToTemplate || !!removingTemplate || !!settingAsDefault}
                                                            onPressed={(e) => e.preventDefault() || e.stopPropagation() || applyCurrentFiltersToTemplate(template)}
                                                        >
                                                            {
                                                                applyingChangesToTemplate === template.id
                                                                    ? <CircularProgress size={16}/>
                                                                    : <RefreshRounded/>
                                                            }
                                                        </IconButton>
                                                    </Box>
                                                </Tooltip>
                                                <Tooltip title={settingAsDefault === template.id ? "Setting as default..." : template.isDefault ? "Default Template" : "Set as Default Template"}
                                                         placement="top">
                                                    <Box component={'span'}>
                                                        <IconButton
                                                            size={'small'}
                                                            color={'secondary'}
                                                            disabled={disabled || !!applyingChangesToTemplate || !!removingTemplate || !!settingAsDefault}
                                                            onPressed={(e) => e.preventDefault() || e.stopPropagation() || setAsDefaultTemplate(template)}
                                                        >
                                                            {
                                                                settingAsDefault === template.id
                                                                    ? <CircularProgress size={16}/>
                                                                    : template.isDefault
                                                                        ? <StarRounded className={'icon normal'}/>
                                                                        : <StarBorderOutlined className={'icon normal'}/>
                                                            }
                                                        </IconButton>
                                                    </Box>
                                                </Tooltip>
                                                <Tooltip title={"Edit Template"} placement="top">
                                                    <Box component={'span'}>
                                                        <IconButton
                                                            size={'small'}
                                                            color={'primary'}
                                                            disabled={disabled || !!applyingChangesToTemplate || !!removingTemplate || !!settingAsDefault}
                                                            onPressed={(e) => e.preventDefault() || e.stopPropagation() || setDialogState({open: true, templateToEdit: template})}
                                                        >
                                                            <EditOutlined className={'icon normal'}/>
                                                        </IconButton>
                                                    </Box>
                                                </Tooltip>
                                                <Tooltip title={removingTemplate === template.id ? "Removing Template..." : "Remove Template"} placement="top">
                                                    <Box component={'span'}>
                                                        <IconButton
                                                            size={'small'}
                                                            color={'error'}
                                                            disabled={disabled || !!applyingChangesToTemplate || !!removingTemplate || !!settingAsDefault}
                                                            onPressed={(e) => e.preventDefault() || e.stopPropagation() || removeTemplate(template)}
                                                        >
                                                            {
                                                                removingTemplate === template.id
                                                                    ? <CircularProgress size={16}/>
                                                                    : <DeleteOutline className={'icon normal'}/>
                                                            }
                                                        </IconButton>
                                                    </Box>
                                                </Tooltip>
                                            </Box>
                                        </Card>
                                    ))
                                }
                                <Button
                                    variant="text"
                                    disabled={disabled || loading}
                                    onClick={() => setDialogState({open: true, templateToEdit: undefined})}
                                    sx={{marginBlockEnd: templates.length === 0 ? 2 : 0}}
                                >
                                    {
                                        saving
                                            ? 'Creating Template...'
                                            : "Create New Template"
                                    }
                                </Button>
                            </Box>
                        }
                    </Box>
                </Container>
            </Popover>
            <UpsertTemplateDialog
                open={dialogState.open}
                templateToEdit={dialogState.templateToEdit}
                onClose={() => setDialogState({open: false, templateToEdit: undefined})}
                onSave={createOrUpdateTemplate}
            />
        </>
    );
}


interface UpsertTemplateDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: (template: Partial<DashboardFilterTemplate>) => Promise<void>;
    templateToEdit?: DashboardFilterTemplate;
}

const UpsertTemplateDialog: React.FC<UpsertTemplateDialogProps> = ({open, onClose, onSave, templateToEdit}) => {
    const [submitting, setSubmitting] = useState(false);
    const isMounted = useIsMounted();
    const formId = useMemo(() => createUUId(), []);

    const schema = useMemo(() => Yup.object().shape({
        name: Yup.string().trim().nullable().required(ValidateMessages.required),
    }), []);
    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);
    const initialValues = useMemo(() => ({
        name: templateToEdit?.name || '',
    }), [templateToEdit]);

    /**
     * Edit printer hub.
     * @type {(function(*): Promise<void>)}
     */
    const submitForm = useCallback(async (values) => {
        setSubmitting(true);
        await onSave({...templateToEdit, name: values.name,});
        if (!isMounted())
            return;
        setSubmitting(false);
        onClose();
    }, [isMounted, onClose, onSave, templateToEdit]);

    return (
        <>
            <Dialog open={open} setOpen={onClose} maxWidth={'sm'} fullWidth>
                <DialogTitle>
                    {templateToEdit ? 'Edit Template' : 'Create New Template'}
                </DialogTitle>
                <DialogContent dividers>
                    <Form<Schema, Partial<Schema>>
                        onSubmit={submitForm}
                        validate={validate}
                        initialValues={initialValues}
                        formProps={{id: formId}}
                    >
                        <Grid2 spacing={2} container paddingBlock={4}>
                            <Grid2 xs={12}>
                                <MuiRFF.TextField
                                    name={'name'}
                                    label={'Name'}
                                    required={required.name}
                                />
                            </Grid2>
                        </Grid2>
                    </Form>
                </DialogContent>
                <DialogActions className={'p-0 m-0'}>
                    <Box sx={{margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                        <Button
                            disabled={submitting}
                            variant={'dialog'}
                            color={'primary'}
                            onClick={() => onClose()}
                            borderRadius={'0 8px 0 8px'}
                        >
                            Close
                        </Button>
                        <Button
                            disabled={submitting}
                            variant={'dialog'}
                            color={'primary'}
                            form={formId}
                            type={'submit'}
                            borderRadius={'8px 0 8px 0'}
                        >
                            {
                                !submitting &&
                                (templateToEdit ? 'Save Changes' : 'Create Template')
                            }
                            {
                                submitting &&
                                (templateToEdit ? 'Saving...' : 'Creating...')
                            }
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </>
    );
}

